import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { Skeleton } from '../components/ui/skeleton';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Download,
  Save,
  Loader2,
  Check,
  X,
  Clock,
  Sun,
  CalendarOff
} from 'lucide-react';

const STATUS_OPTIONS = [
  { value: 'present', label: 'Present', color: 'bg-green-500', icon: Check },
  { value: 'absent', label: 'Absent', color: 'bg-red-500', icon: X },
  { value: 'half-day', label: 'Half Day', color: 'bg-yellow-500', icon: Clock },
  { value: 'holiday', label: 'Holiday', color: 'bg-blue-500', icon: Sun },
  { value: 'weekend', label: 'Weekend', color: 'bg-gray-400', icon: CalendarOff },
];

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const getDaysInMonth = (month, year) => new Date(year, month, 0).getDate();

const getDayOfWeek = (day, month, year) => {
  const date = new Date(year, month - 1, day);
  return date.getDay();
};

export const Timesheet = () => {
  const [searchParams] = useSearchParams();
  const personnelIdParam = searchParams.get('personnel');
  const { api } = useAuth();
  
  const [personnel, setPersonnel] = useState([]);
  const [selectedPersonnel, setSelectedPersonnel] = useState(personnelIdParam || '');
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  const daysInMonth = useMemo(() => getDaysInMonth(month, year), [month, year]);

  const fetchPersonnel = async () => {
    try {
      const response = await api.get('/personnel', { params: { is_active: true } });
      setPersonnel(response.data);
      if (!selectedPersonnel && response.data.length > 0) {
        setSelectedPersonnel(response.data[0].id);
      }
    } catch (error) {
      toast.error('Failed to load personnel');
    }
  };

  const fetchTimesheet = async () => {
    if (!selectedPersonnel) return;
    
    setLoading(true);
    try {
      const response = await api.get(`/timesheets/${selectedPersonnel}/${year}/${month}`);
      setEntries(response.data.entries || []);
    } catch (error) {
      if (error.response?.status === 404) {
        // Initialize empty entries for new timesheet
        const newEntries = [];
        for (let day = 1; day <= daysInMonth; day++) {
          const dayOfWeek = getDayOfWeek(day, month, year);
          const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
          newEntries.push({
            date: `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
            status: isWeekend ? 'weekend' : 'present',
            hours: isWeekend ? 0 : 8,
            notes: ''
          });
        }
        setEntries(newEntries);
      } else {
        toast.error('Failed to load timesheet');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPersonnel();
  }, [api]);

  useEffect(() => {
    if (selectedPersonnel) {
      fetchTimesheet();
    }
  }, [selectedPersonnel, month, year]);

  const handleStatusChange = (index, status) => {
    const newEntries = [...entries];
    newEntries[index] = {
      ...newEntries[index],
      status,
      hours: status === 'present' ? 8 : status === 'half-day' ? 4 : 0
    };
    setEntries(newEntries);
  };

  const handleSave = async () => {
    if (!selectedPersonnel) return;
    
    setSaving(true);
    try {
      await api.post('/timesheets', {
        personnel_id: selectedPersonnel,
        month,
        year,
        entries
      });
      toast.success('Timesheet saved successfully');
    } catch (error) {
      toast.error('Failed to save timesheet');
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    if (!selectedPersonnel) return;
    
    setExporting(true);
    try {
      const response = await api.get(`/export/timesheet/${selectedPersonnel}/${year}/${month}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const person = personnel.find(p => p.id === selectedPersonnel);
      link.setAttribute('download', `timesheet_${person?.surname || 'personnel'}_${month}_${year}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Timesheet exported successfully');
    } catch (error) {
      toast.error('Failed to export timesheet');
    } finally {
      setExporting(false);
    }
  };

  const navigateMonth = (direction) => {
    if (direction === 'prev') {
      if (month === 1) {
        setMonth(12);
        setYear(year - 1);
      } else {
        setMonth(month - 1);
      }
    } else {
      if (month === 12) {
        setMonth(1);
        setYear(year + 1);
      } else {
        setMonth(month + 1);
      }
    }
  };

  const summary = useMemo(() => {
    const present = entries.filter(e => e.status === 'present').length;
    const absent = entries.filter(e => e.status === 'absent').length;
    const halfDay = entries.filter(e => e.status === 'half-day').length;
    const totalHours = entries.reduce((sum, e) => sum + (e.hours || 0), 0);
    return { present, absent, halfDay, totalHours };
  }, [entries]);

  const selectedPerson = personnel.find(p => p.id === selectedPersonnel);

  return (
    <div className="page-transition space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Timesheet (Puantaj)</h1>
          <p className="text-muted-foreground">
            Daily attendance tracking and monthly overview
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleExport}
            disabled={!selectedPersonnel || exporting}
            data-testid="export-timesheet-btn"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Export Excel
          </Button>
          <Button
            onClick={handleSave}
            className="btn-primary"
            disabled={!selectedPersonnel || saving}
            data-testid="save-timesheet-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save
          </Button>
        </div>
      </div>

      {/* Controls */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <Select value={selectedPersonnel} onValueChange={setSelectedPersonnel}>
              <SelectTrigger className="w-full sm:w-64" data-testid="select-personnel-timesheet">
                <SelectValue placeholder="Select personnel" />
              </SelectTrigger>
              <SelectContent>
                {personnel.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name} {p.surname}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={() => navigateMonth('prev')} data-testid="prev-month">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="flex items-center gap-2 px-4 py-2 bg-muted rounded-lg min-w-[180px] justify-center">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <span className="font-medium">{MONTHS[month - 1]} {year}</span>
              </div>
              <Button variant="outline" size="icon" onClick={() => navigateMonth('next')} data-testid="next-month">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-sm text-muted-foreground">Present</p>
            <p className="text-2xl font-bold text-green-500">{summary.present}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-sm text-muted-foreground">Absent</p>
            <p className="text-2xl font-bold text-red-500">{summary.absent}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-sm text-muted-foreground">Half Days</p>
            <p className="text-2xl font-bold text-yellow-500">{summary.halfDay}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-sm text-muted-foreground">Total Hours</p>
            <p className="text-2xl font-bold text-primary">{summary.totalHours}</p>
          </CardContent>
        </Card>
      </div>

      {/* Timesheet Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" />
            {selectedPerson ? `${selectedPerson.name} ${selectedPerson.surname}` : 'Select Personnel'}
            <Badge variant="secondary" className="ml-2">
              {MONTHS[month - 1]} {year}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8">
              <Skeleton className="h-96 w-full" />
            </div>
          ) : !selectedPersonnel ? (
            <div className="p-12 text-center text-muted-foreground">
              Select a personnel to view/edit timesheet
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-24">Day</TableHead>
                  <TableHead className="w-32">Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-20">Hours</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((entry, index) => {
                  const date = new Date(entry.date);
                  const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
                  const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                  const statusOption = STATUS_OPTIONS.find(s => s.value === entry.status);
                  
                  return (
                    <TableRow 
                      key={entry.date} 
                      className={isWeekend ? 'bg-muted/30' : ''}
                      data-testid={`timesheet-row-${index}`}
                    >
                      <TableCell className="font-medium">{dayName}</TableCell>
                      <TableCell>{new Date(entry.date).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Select
                          value={entry.status}
                          onValueChange={(value) => handleStatusChange(index, value)}
                        >
                          <SelectTrigger className="w-32" data-testid={`status-select-${index}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUS_OPTIONS.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                <div className="flex items-center gap-2">
                                  <div className={`w-2 h-2 rounded-full ${option.color}`} />
                                  {option.label}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Badge variant={entry.hours > 0 ? 'default' : 'secondary'}>
                          {entry.hours}h
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Timesheet;
